import { graphHooks } from '@tunasong/graph-lib/react'
import { dayjs } from '@tunasong/models'
import { workflowsApi } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import {
  Divider,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'
import { EntitySelectDialog } from '../entity/entity-select-dialog.js'

export interface JobsProps {}

/**
 * What an execution exported. An account export has no root; a space export names the
 * space (spec §4) — by name while the user can still read it, by id once they cannot,
 * so the download stays identifiable either way.
 */
const ExportScope: FC<{ rootEntityId?: string }> = ({ rootEntityId }) => {
  const { entity } = graphHooks.useEntity(rootEntityId)
  if (!rootEntityId) {
    return <>Everything</>
  }
  return <>Space: {entity?.name ?? rootEntityId}</>
}

export const Exports: FC<JobsProps> = props => {
  const {} = props
  const { currentData: exports = [], isLoading } = workflowsApi.useGetExportsQuery()
  const [doExport, exportStatus] = workflowsApi.useExportMutation()
  const [pickingSpace, setPickingSpace] = useState(false)

  const isRunning = exports[0]?.status === 'RUNNING'
  const disabled = exportStatus.isSuccess || exportStatus.isLoading || isRunning

  const handleExportAll = () => {
    doExport()
  }

  const handleSpacePicked = (selected?: Persisted<Entity>) => {
    setPickingSpace(false)
    if (selected) {
      doExport({ rootEntityId: selected.id })
    }
  }

  if (isLoading) {
    return <LinearProgress />
  }
  return (
    <div>
      <Button variant="contained" color="primary" onClick={handleExportAll} disabled={disabled}>
        Export my data
      </Button>{' '}
      <Button variant="outlined" color="primary" onClick={() => setPickingSpace(true)} disabled={disabled}>
        Export a space…
      </Button>
      <EntitySelectDialog
        open={pickingSpace}
        title="Export a space"
        description="Exports the space and everything in it as a zip a local Tunasong can open directly."
        onClose={handleSpacePicked}
      />
      {isRunning ? <Typography variant="subtitle1">Export is running. This may take a while...</Typography> : null}
      <Divider>Exports</Divider>
      <List>
        {exports.map(e => (
          <ListItem key={e.name}>
            <ListItemText
              primary={dayjs(e.stopDate).format('YYYY-MM-DD')}
              secondary={
                <>
                  {e.status} · <ExportScope rootEntityId={e.rootEntityId} />
                </>
              }
            />
            <ListItemSecondaryAction>
              <Button
                href={e.url ?? undefined}
                target="_blank"
                variant="outlined"
                color="primary"
                disabled={e.status !== 'SUCCEEDED' || !e.url}
              >
                Download
              </Button>
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </div>
  )
}

export default Exports
